import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  fetchFolderEffect,
  fetchSongListEffect,
  handleCreateSongWithDeeplinkingEffect,
} from 'app/store/SongList/effects';
import {
  createSongLoadingStateSelector,
  getCreateSongLinkSelector,
  getFolderSongs,
  getSongListNextLink,
  getFolderSongListNextLink,
  getSongListSelector,
} from 'app/store/SongList/selectors';
import { updateSiteSettings, useInfiniteLoader } from 'app/utils/hooks';
import { FolderItem, Song } from 'types/song';
import { selectSongAction } from 'app/store/Mixpanel/slice';
import { getSortParams } from 'app/components/ChakraTableList';
import { routes } from 'app/routing/routesConfig';
import {
  getAudioPlayer,
  getIsPlayerLoading,
  getPlayerCurrentAudio,
} from 'app/store/AudioPlayer/selectors';
import { useAudioPlayer } from 'app/components/AudioPlayer/hooks';
import { DeeplinkActions, LoadingState } from 'types/enums';
import { SortParams } from 'types/generic.types';
import { isEqual } from 'lodash';
import {
  getFolderLocalStorageKey,
  getInitialSortParams,
  isSongsSortBy,
  SongsSortBy,
} from './utils';
import { SongListComponent } from './SongListComponent';

interface SongListProps {
  folder?: FolderItem;
}

export const SongList = ({ folder }: SongListProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search, state } = useLocation();

  const allSongs = useAppSelector(getSongListSelector);
  const folderSongs = useAppSelector(getFolderSongs);

  const songs = useMemo(() => (folder?.guid ? folderSongs : allSongs), [
    allSongs,
    folder?.guid,
    folderSongs,
  ]);

  const canCreateSong = Boolean(useAppSelector(getCreateSongLinkSelector));
  const createSongLoadingState = useAppSelector(createSongLoadingStateSelector);
  const isCreateSongLoading = createSongLoadingState === LoadingState.FETCHING;
  const [isLoading, setIsLoading] = useState(true);

  const { loadAudio, playPauseAudio } = useAudioPlayer();
  const isPlayerLoading = useAppSelector(getIsPlayerLoading);
  const { isPlaying } = useAppSelector(getAudioPlayer);
  const currentAudio = useAppSelector(getPlayerCurrentAudio);

  const prevSortParams = useRef<SortParams<SongsSortBy>>();
  const prevFolderGuid = useRef<string | undefined>();

  const urlParams = useMemo(() => new URLSearchParams(search), [search]);

  const localStorageKey = useMemo(() => getFolderLocalStorageKey(folder), [
    folder,
  ]);

  const initialSortParams = useMemo(
    () => getInitialSortParams(localStorageKey),
    [localStorageKey]
  );

  const sortParams = useMemo(
    () =>
      getSortParams<SongsSortBy>(urlParams, initialSortParams, isSongsSortBy),
    [urlParams, initialSortParams]
  );

  const handleCreateSong = async (args) => {
    try {
      const { guid: newSongGuid } = await dispatch(
        handleCreateSongWithDeeplinkingEffect(args)
      ).unwrap();

      if (newSongGuid) {
        navigate(routes.songAudioList.path.replace(':guid', newSongGuid), {
          state: { shouldNotFetch: true },
        });
      }
    } catch (e: any) {
      if (e?.shouldRedirectToAuth) {
        navigate('/', {
          state: {
            from: routes.songs.path,
            action: {
              type: DeeplinkActions.ADD_SONG,
              metadata: e.data,
            },
          },
          replace: true,
        });
      }
    }
  };

  const fetchSongs = useCallback(
    async (sortParams, excludeSongGuid) => {
      setIsLoading(true);
      if (folder?.guid) {
        await dispatch(
          fetchFolderEffect({
            guid: folder.guid,
            sortParams,
          })
        );
      } else {
        await dispatch(
          fetchSongListEffect({
            sortParams,
            excludeSongGuid,
          })
        );
      }
      setIsLoading(false);
      updateSiteSettings(localStorageKey, sortParams);
    },
    [dispatch, folder?.guid, localStorageKey]
  );

  const [, infiniteScrollProps, loadingMore] = useInfiniteLoader({
    dataLength: songs.length,
    ...(folder?.guid
      ? {
          nextLinkSelector: getFolderSongListNextLink,
          fetchNext: (next) => fetchFolderEffect({ next }),
        }
      : {
          nextLinkSelector: getSongListNextLink,
          fetchNext: (next) => fetchSongListEffect({ next }),
        }),
  });

  const handlePlay = async (song: Song) => {
    if (song.latestVersionAudio) {
      if (currentAudio?.audioGuid === song.latestVersionAudio) {
        playPauseAudio();
      } else {
        await loadAudio({
          audio: [
            {
              audioGuid: song.latestVersionAudio,
              parentGuid: song.guid,
              songStatus: song.status,
              songTitle: song.title,
            },
          ],
        });
      }
    }
  };

  const handleRowClick = (guid: string) => {
    dispatch(selectSongAction(guid));
  };

  useEffect(() => {
    if (
      !isEqual(sortParams, prevSortParams.current) ||
      !isEqual(folder?.guid, prevFolderGuid.current)
    ) {
      fetchSongs(sortParams, state?.excludeSongGuid);
      prevSortParams.current = sortParams;
      prevFolderGuid.current = folder?.guid;
    }
  }, [folder?.guid, state, fetchSongs, sortParams]);

  useEffect(() => {
    if (
      canCreateSong &&
      (state as any)?.action?.type === DeeplinkActions.ADD_SONG
    ) {
      handleCreateSong({
        data: (state as any).action.metadata,
        isFromProTools: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canCreateSong]);

  return (
    <SongListComponent
      folder={folder}
      dispatch={dispatch}
      songs={songs}
      canCreateSong={canCreateSong}
      isCreateSongLoading={isCreateSongLoading}
      loading={isLoading}
      onNewSong={() => handleCreateSong({ data: {} })}
      infiniteScrollProps={infiniteScrollProps}
      loadingMore={loadingMore}
      sortParams={sortParams}
      onRowClick={handleRowClick}
      audioGuid={currentAudio?.audioGuid || ''}
      isPlaying={isPlaying}
      handlePlay={handlePlay}
      isAudioLoading={isPlayerLoading}
    />
  );
};
